import { useTranslation } from 'react-i18next'

import { Text } from '@enechain/ecloud-designsystem'

import {
  type FeeAutoConsiderPreview,
  feeAutoConsiderHint,
} from '~/models/feeAutoConsiderPreview'
import {
  spotFeeAdjustmentViolationLines,
  spotFeeHintKey,
} from '~/pages/SpotBidding/models/feePreview'
import type { SpotFeeAutoConsider } from '~/pages/SpotBidding/models/spotFeeAutoConsider'

type Props = {
  /** 手数料自動考慮 (スポット) の扱い (`useSpotFeeAutoConsider`)。 */
  fee: SpotFeeAutoConsider
  /** 一括作成は「全札の」文言を使う (AC-BSP-004b / AC-BSP-005 の案内文言差分)。 */
  variant?: 'individual' | 'bulk'
  /**
   * 扱いを固定した後に、 入札制限 (ON/OFF の設定を含む) を読めなくなった間 (再取得の失敗)。
   * 保存を止めている理由として、 `fee` が取得中・取得失敗でなくても取得失敗の案内を出す。
   */
  isSettingsUnavailable?: boolean
  /** backend のプレビュー RPC の判定 (`useSpotFeeAutoConsiderPreview`)。 入札価格と範囲外はこの判定から出す。 */
  preview: FeeAutoConsiderPreview
}

/**
 * 手数料自動考慮 (`bid-limits.md` AC-BDL-016、`spot-bidding.md` AC-BSP-004b) の
 * 価格欄の補足表示。 個別作成モーダル ([[SpotBidCreateModal]] / [[IndividualBidModal]])・
 * 一括作成モーダル ([[BulkCreateModal]])・ブロック入札のモーダル ([[BlockBidCreateModal]]) で
 * 共有する (spec が同一算出ロジック・類似案内文言を要求するため、画面ごとの写経を禁止する)。
 */
export const FeeAutoConsiderHint = ({
  fee,
  variant = 'individual',
  isSettingsUnavailable = false,
  preview,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  if (isSettingsUnavailable && fee.kind !== 'loading') {
    return (
      <Text color="Text.negativeMid" fontSize="sm">
        {t('page:biddingSpot.form.feeAutoConsiderUnavailableHint')}
      </Text>
    )
  }

  // design は範囲外 (AC-BSP-106) を補足表示の欄にその場で 2 行出し、 1 行目を強調する。
  if (preview.kind === 'outOfRange') {
    const { line1, line2 } = spotFeeAdjustmentViolationLines(
      t,
      preview.violation,
    )
    return (
      <Text color="Text.negativeHigh" fontSize="sm">
        <Text as="span" display="block" fontWeight="semibold">
          {line1}
        </Text>
        <Text as="span" display="block">
          {line2}
        </Text>
      </Text>
    )
  }
  // 入札価格を backend に問い合わせている間は、 待っている対象 (入札価格の算出) を出す (Des #11471 Q-C = B)。
  // 設定が OFF の組織は OFF の案内のまま (計算中の文へ切り替えない)。
  const isCalculating =
    variant === 'bulk' &&
    preview.kind === 'blocked' &&
    preview.reason === 'loading'
  if (isCalculating && fee.kind === 'on') {
    return (
      <Text color="Text.midEmphasis" fontSize="sm">
        {t('page:biddingSpot.form.feeAutoConsiderCalculatingBulk')}
      </Text>
    )
  }
  const hint = feeAutoConsiderHint(
    isCalculating && fee.kind === 'off'
      ? { kind: 'ready', confirmed: { enabled: false } }
      : preview,
  )
  return (
    <Text color={hint.color} fontSize="sm">
      {t(
        variant === 'bulk' && hint.key === 'feeAutoConsiderAdjusted'
          ? 'page:biddingSpot.form.feeAutoConsiderOnPreviewBulk'
          : variant === 'bulk' && hint.key === 'feeAutoConsiderOff'
            ? 'page:biddingSpot.form.feeAutoConsiderOffHintBulk'
            : spotFeeHintKey(hint),
        { price: hint.orderPriceYenPerKwh },
      )}
    </Text>
  )
}
